/**
 * Main Entry Point
 * React 18 application entry
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { ConfigProvider } from 'antd';
import { getAntdThemeConfig, getThemeScssVariables, CLIENT_ID } from './config/theme.config';
import config from './config/environment.config';
import { store, persistor } from './store';
import App from './App';
import ErrorBoundary from './components/common/ErrorBoundary/ErrorBoundary';
import './styles/main.scss';
// Import axios interceptors to ensure Authorization header is set
import './api/interceptors';

// Silence console output in production (keep console.error for real issues)
if (!config.enableLogging) {
  // eslint-disable-next-line no-console
  console.log = () => {};
  // eslint-disable-next-line no-console
  console.info = () => {};
  // eslint-disable-next-line no-console
  console.debug = () => {};
  // eslint-disable-next-line no-console
  console.warn = () => {};
}

// Get theme configuration for current client
const themeConfig = getAntdThemeConfig(CLIENT_ID);

// Get CSS variables for the theme
const themeVariables = getThemeScssVariables(CLIENT_ID);

// Apply theme to document
document.documentElement.setAttribute('data-theme', CLIENT_ID);
document.body.setAttribute('data-theme', CLIENT_ID);

// Inject CSS variables into root element
Object.entries(themeVariables).forEach(([key, value]) => {
  document.documentElement.style.setProperty(key, value);
});

// Force enable text selection globally
const enableTextSelection = () => {
  // Inject CSS with highest priority
  const style = document.createElement('style');
  style.id = 'force-text-selection';
  style.textContent = `
    html, body, #root, .app, * {
      -webkit-user-select: text !important;
      -moz-user-select: text !important;
      -ms-user-select: text !important;
      user-select: text !important;
    }
    button, .ant-btn, input[type="button"], input[type="submit"], input[type="reset"], button *, .ant-btn * {
      -webkit-user-select: none !important;
      -moz-user-select: none !important;
      -ms-user-select: none !important;
      user-select: none !important;
    }
    ::selection {
      background-color: #b3d4fc !important;
      color: #000000 !important;
      -webkit-text-fill-color: #000000 !important;
    }
    ::-moz-selection {
      background-color: #b3d4fc !important;
      color: #000000 !important;
    }
    *::selection {
      background-color: #b3d4fc !important;
      color: #000000 !important;
      -webkit-text-fill-color: #000000 !important;
    }
    *::-moz-selection {
      background-color: #b3d4fc !important;
      color: #000000 !important;
    }
  `;
  document.head.appendChild(style);

  // Remove any event listeners that might prevent selection
  document.addEventListener('selectstart', (e) => {
    e.stopPropagation();
  }, true);

  document.addEventListener('mousedown', () => {
    // Allow selection - don't prevent default
  }, true);

  // Also apply to body when it's ready
  if (document.body) {
    document.body.style.setProperty('-webkit-user-select', 'text');
    document.body.style.setProperty('-moz-user-select', 'text');
    document.body.style.setProperty('-ms-user-select', 'text');
    document.body.style.userSelect = 'text';
  }

  // Apply to root when it's ready
  const root = document.getElementById('root');
  if (root) {
    root.style.setProperty('-webkit-user-select', 'text');
    root.style.setProperty('-moz-user-select', 'text');
    root.style.setProperty('-ms-user-select', 'text');
    root.style.userSelect = 'text';
  }
};

// Enable text selection immediately
enableTextSelection();

// Also enable after DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', enableTextSelection);
} else {
  enableTextSelection();
}

// Enable after a short delay to ensure all styles are loaded
setTimeout(enableTextSelection, 100);
setTimeout(enableTextSelection, 500);

const container = document.getElementById('root');

if (!container) {
  throw new Error('Failed to find root element');
}

const root = createRoot(container);

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <BrowserRouter>
            <ConfigProvider 
              theme={themeConfig}
              modal={{
                centered: true, // Center all modals by default
              }}
            >
              <App />
            </ConfigProvider>
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  </React.StrictMode>
);

